import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'https://kdu-hub-production.up.railway.app';

interface Venue {
  name: string;
  address: string;
  city: string;
  state: string;
}

interface AgendaItem {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  speaker: string | null;
}

interface Vendor {
  id: string;
  businessName: string;
  category: string | null;
  contactPerson: string | null;
  phone: string | null;
  boothNumber: string | null;
  zone: string | null;
  description: string | null;
  catalogItems: CatalogItem[];
}

interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  imageUrl: string | null;
  category: string | null;
  featured: boolean;
}

interface GuestPassEvent {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venue: Venue;
  mealInfo: string | null;
  dressCode: string | null;
  transportInfo: string | null;
  parkingInfo: string | null;
  specialMessage: string | null;
  programUrl: string | null;
  agenda: AgendaItem[];
  vendors: Vendor[];
}

type View = 'landing' | 'details' | 'subscribe';

// ── Vendor Detail Popup ────────────────────────────────────────

function VendorDetailPopup({ vendor, onClose }: { vendor: Vendor; onClose: () => void }) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const hasDetails = !!(vendor.description || vendor.boothNumber || vendor.zone || vendor.contactPerson || vendor.phone || (vendor.catalogItems && vendor.catalogItems.length > 0));

  return (
    <div style={popupStyles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={`${vendor.businessName} details`}>
      <div style={popupStyles.sheet} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={popupStyles.header}>
          <div style={popupStyles.vendorIcon}>🏪</div>
          <div style={{ flex: 1 }}>
            <h3 style={popupStyles.vendorName}>{vendor.businessName}</h3>
            {vendor.category && <span style={popupStyles.categoryBadge}>{vendor.category}</span>}
          </div>
          <button style={popupStyles.closeBtn} onClick={onClose} aria-label="Close vendor details">✕</button>
        </div>

        {/* Description */}
        {vendor.description && (
          <p style={popupStyles.description}>{vendor.description}</p>
        )}

        {/* Info pills */}
        {(vendor.boothNumber || vendor.zone) && (
          <div style={popupStyles.pillRow}>
            {vendor.boothNumber && (
              <div style={popupStyles.pill}>
                <span style={popupStyles.pillIcon}>📍</span>
                <span style={popupStyles.pillLabel}>Booth</span>
                <span style={popupStyles.pillValue}>{vendor.boothNumber}</span>
              </div>
            )}
            {vendor.zone && (
              <div style={{ ...popupStyles.pill, backgroundColor: '#f0f9ff', borderColor: '#bae6fd' }}>
                <span style={popupStyles.pillIcon}>🗺️</span>
                <span style={popupStyles.pillLabel}>Zone</span>
                <span style={{ ...popupStyles.pillValue, color: '#0369a1' }}>{vendor.zone}</span>
              </div>
            )}
          </div>
        )}

        {/* Contact info */}
        {(vendor.contactPerson || vendor.phone) && (
          <div style={popupStyles.contactSection}>
            {vendor.contactPerson && (
              <div style={popupStyles.contactRow}>
                <span style={{ marginRight: '8px' }}>👤</span>
                <span style={popupStyles.contactText}>{vendor.contactPerson}</span>
              </div>
            )}
            {vendor.phone && (
              <div style={popupStyles.contactRow}>
                <span style={{ marginRight: '8px' }}>📞</span>
                <a href={`tel:${vendor.phone}`} style={popupStyles.contactLink}>{vendor.phone}</a>
              </div>
            )}
          </div>
        )}

        {/* Catalog Items */}
        {vendor.catalogItems && vendor.catalogItems.length > 0 && (
          <div style={popupStyles.catalogSection}>
            <h4 style={popupStyles.catalogHeading}>What We Offer</h4>
            <div style={popupStyles.catalogGrid}>
              {vendor.catalogItems.map((item) => (
                <div key={item.id} style={popupStyles.catalogCard}>
                  {item.imageUrl && (
                    <div
                      style={{ ...popupStyles.catalogImgWrap, cursor: 'pointer' }}
                      onClick={() => setLightboxUrl(item.imageUrl!.startsWith('http') ? item.imageUrl! : `${API_BASE}${item.imageUrl}`)}
                      role="button"
                      aria-label={`View full image of ${item.name}`}
                    >
                      <img
                        src={item.imageUrl.startsWith('http') ? item.imageUrl : `${API_BASE}${item.imageUrl}`}
                        alt={item.name}
                        style={popupStyles.catalogImg}
                      />
                    </div>
                  )}
                  <div style={popupStyles.catalogBody}>
                    <div style={popupStyles.catalogName}>
                      {item.featured && <span style={{ marginRight: '4px' }}>⭐</span>}
                      {item.name}
                    </div>
                    {item.description && <div style={popupStyles.catalogDesc}>{item.description}</div>}
                    {item.price != null && Number(item.price) > 0 && (
                      <div style={popupStyles.catalogPrice}>${Number(item.price).toFixed(2)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fallback when no extra details are available */}
        {!hasDetails && (
          <div style={popupStyles.fallback}>
            <p style={popupStyles.fallbackText}>This vendor will be present at the event. More details coming soon!</p>
          </div>
        )}

        {/* Close button at bottom */}
        <button style={popupStyles.dismissBtn} onClick={onClose}>Close</button>

        {/* Image Lightbox */}
        {lightboxUrl && (
          <div
            style={popupStyles.lightboxOverlay}
            onClick={() => setLightboxUrl(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Full size image"
          >
            <button
              style={popupStyles.lightboxClose}
              onClick={() => setLightboxUrl(null)}
              aria-label="Close image"
            >
              ✕
            </button>
            <img
              src={lightboxUrl}
              alt="Full size"
              style={popupStyles.lightboxImg}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const popupStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  sheet: {
    width: '100%',
    maxWidth: '380px',
    maxHeight: '80vh',
    backgroundColor: '#fff',
    borderRadius: '20px',
    overflowY: 'auto',
    padding: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
  },
  vendorIcon: {
    fontSize: '1.6rem',
    width: '42px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0fdf4',
    borderRadius: '12px',
    flexShrink: 0,
  },
  vendorName: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 4px',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6b7280',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '8px',
    lineHeight: 1,
  },
  description: {
    fontSize: '0.85rem',
    color: '#4b5563',
    lineHeight: 1.5,
    margin: '0 0 14px',
    padding: '10px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
  },
  pillRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '14px',
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '10px',
  },
  pillIcon: { fontSize: '0.9rem' },
  pillLabel: { fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' },
  pillValue: { fontSize: '0.88rem', fontWeight: 700, color: '#166534' },
  contactSection: {
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
    marginBottom: '14px',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '6px',
  },
  contactText: { fontSize: '0.85rem', color: '#374151', fontWeight: 500 },
  contactLink: { fontSize: '0.85rem', color: '#1a6b3c', fontWeight: 600, textDecoration: 'none' },
  catalogSection: {
    marginTop: '4px',
  },
  catalogHeading: {
    fontSize: '0.82rem',
    fontWeight: 700,
    color: '#374151',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  catalogGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  catalogCard: {
    display: 'flex',
    gap: '10px',
    padding: '10px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
  },
  catalogImgWrap: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: '#f3f4f6',
  },
  catalogImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  catalogBody: {
    flex: 1,
    minWidth: 0,
  },
  catalogName: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '2px',
  },
  catalogDesc: {
    fontSize: '0.72rem',
    color: '#6b7280',
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  catalogPrice: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#1a6b3c',
    marginTop: '4px',
  },
  fallback: {
    textAlign: 'center',
    padding: '16px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    marginBottom: '12px',
  },
  fallbackText: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: 0,
    lineHeight: 1.5,
  },
  dismissBtn: {
    display: 'block',
    width: '100%',
    padding: '12px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '4px',
  },
  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '16px',
  },
  lightboxClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    fontSize: '1.4rem',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightboxImg: {
    maxWidth: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '8px',
  },
};

export function GuestPassPage() {
  const { slug } = useParams<{ slug: string }>();
  const [event, setEvent] = useState<GuestPassEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>('landing');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [vendorFilter, setVendorFilter] = useState<string>('All');

  // Subscribe form
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeResult, setSubscribeResult] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/guest-pass/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('not found');
        return res.json();
      })
      .then((data) => setEvent(data))
      .catch(() => setError('Event not found or GuestPass is not available.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubscribe = async () => {
    if (!contactValue.trim() || !slug) return;
    setSubscribing(true);
    setSubscribeResult('');
    try {
      const body = contactType === 'email' ? { email: contactValue.trim() } : { phone: contactValue.trim() };
      const res = await fetch(`${API_BASE}/guest-pass/${slug}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubscribeResult(data.message || 'Subscription failed. Please try again.');
      } else {
        setSubscribeResult(data.message || 'Subscribed!');
        setContactValue('');
      }
    } catch {
      setSubscribeResult('Unable to connect. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const formatTime = (ts: string): string => {
    if (!ts) return '';
    const timePart = ts.includes('T') ? ts.split('T')[1].slice(0, 5) : ts;
    const [h, m] = timePart.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCard}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎫</div>
          <p style={{ color: '#666' }}>Loading event...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !event) {
    return (
      <div style={styles.page}>
        <div style={styles.errorCard}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>😕</div>
          <h2 style={{ color: '#333', fontSize: '1.2rem', marginBottom: '8px' }}>Event Not Found</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>{error || 'This GuestPass link is not active.'}</p>
        </div>
      </div>
    );
  }

  // Landing view
  if (view === 'landing') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={{ fontSize: '2.2rem', marginBottom: '6px' }}>🎫</div>
            <div style={styles.brandLabel}>KDU Georgia 2026</div>
            <h1 style={styles.eventName}>{event.name}</h1>
            <p style={styles.eventDate}>{formatDate(event.date)}</p>
            <p style={styles.eventTime}>{formatTime(event.startTime)} — {formatTime(event.endTime)}</p>
          </div>

          <div style={styles.body}>
            {event.specialMessage && (
              <div style={styles.messageBar}>
                <span style={{ marginRight: '6px' }}>📢</span> <span dangerouslySetInnerHTML={{ __html: event.specialMessage }} />
              </div>
            )}

            {event.programUrl && (
              <a
                href={event.programUrl.startsWith('/') ? `${API_BASE}${event.programUrl}` : event.programUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px', padding: '8px 14px', backgroundColor: '#f0f7f2', border: '1px solid #c8e6c9', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, color: '#1a6b3c', textDecoration: 'none' }}
              >
                📄 View Program
              </a>
            )}

            <button style={styles.btnPrimary} onClick={() => setView('details')}>
              View Event Details
            </button>
            <button style={styles.btnSecondary} onClick={() => setView('subscribe')}>
              Get Event Updates
            </button>

            {/* Quick info cards */}
            <div style={styles.infoSection}>
              <div style={styles.infoCard}>
                <span style={styles.infoIcon}>📍</span>
                <div>
                  <div style={styles.infoLabel}>Location</div>
                  <div style={styles.infoText}>{event.venue.name}</div>
                  <div style={styles.infoSubtext}>{event.venue.address}, {event.venue.city}, {event.venue.state}</div>
                </div>
              </div>
              {event.dressCode && (
                <div style={styles.infoCard}>
                  <span style={styles.infoIcon}>👔</span>
                  <div>
                    <div style={styles.infoLabel}>Dress Code</div>
                    <div style={styles.infoText}>{event.dressCode}</div>
                  </div>
                </div>
              )}
              {event.mealInfo && (
                <div style={styles.infoCard}>
                  <span style={styles.infoIcon}>🍽️</span>
                  <div>
                    <div style={styles.infoLabel}>Food & Drinks</div>
                    <div style={styles.infoText}>{event.mealInfo}</div>
                  </div>
                </div>
              )}
            </div>

            <p style={styles.footer}>Powered by KDUHub • No account required</p>
          </div>
        </div>
      </div>
    );
  }

  // Details view
  if (view === 'details') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.headerSmall}>
            <button style={styles.backBtn} onClick={() => setView('landing')}>← Back</button>
            <h2 style={styles.detailTitle}>{event.name}</h2>
            <p style={styles.detailSubtitle}>{formatDate(event.date)} • {formatTime(event.startTime)}</p>
          </div>

          <div style={styles.body}>
            {/* Venue */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>📍 Venue</h3>
              <p style={styles.sectionText}>{event.venue.name}</p>
              <p style={styles.sectionSubtext}>{event.venue.address}, {event.venue.city}, {event.venue.state}</p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${event.venue.address}, ${event.venue.city}, ${event.venue.state}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.mapLink}
              >
                Open in Maps →
              </a>
            </div>

            {/* Transport */}
            {event.transportInfo && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🚌 Transportation</h3>
                <p style={styles.sectionText}>{event.transportInfo}</p>
              </div>
            )}

            {/* Parking */}
            {event.parkingInfo && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🅿️ Parking</h3>
                <p style={styles.sectionText}>{event.parkingInfo}</p>
              </div>
            )}

            {/* Agenda */}
            {event.agenda.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>📋 Schedule</h3>
                {event.agenda.map((item) => (
                  <div key={item.id} style={styles.agendaItem}>
                    <div style={styles.agendaTime}>{formatTime(item.startTime)}</div>
                    <div>
                      <div style={styles.agendaTitle}>{item.title}</div>
                      {item.speaker && <div style={styles.agendaSpeaker}>{item.speaker}</div>}
                      {item.description && <div style={styles.agendaDesc}>{item.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Vendors */}
            {event.vendors.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🏪 Vendors</h3>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 10px' }}>Tap a vendor for details</p>

                {/* Category filter pills */}
                {(() => {
                  const categories = Array.from(new Set(event.vendors.map(v => v.category || 'Other'))).sort();
                  if (categories.length <= 1) return null;
                  return (
                    <div style={styles.filterRow}>
                      <button
                        style={vendorFilter === 'All' ? styles.filterPillActive : styles.filterPill}
                        onClick={() => setVendorFilter('All')}
                      >
                        All ({event.vendors.length})
                      </button>
                      {categories.map(cat => {
                        const count = event.vendors.filter(v => (v.category || 'Other') === cat).length;
                        return (
                          <button
                            key={cat}
                            style={vendorFilter === cat ? styles.filterPillActive : styles.filterPill}
                            onClick={() => setVendorFilter(cat)}
                          >
                            {cat} ({count})
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                <div style={styles.vendorGrid}>
                  {event.vendors
                    .filter(v => vendorFilter === 'All' || (v.category || 'Other') === vendorFilter)
                    .map((v) => (
                    <button
                      key={v.id}
                      style={styles.vendorChip}
                      onClick={() => setSelectedVendor(v)}
                      aria-label={`View details for ${v.businessName}`}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{v.businessName}</div>
                      {v.category && <div style={{ fontSize: '0.7rem', color: '#666' }}>{v.category}</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Meal */}
            {event.mealInfo && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🍽️ Food & Drinks</h3>
                <p style={styles.sectionText}>{event.mealInfo}</p>
              </div>
            )}

            <button style={{ ...styles.btnSecondary, marginTop: '20px' }} onClick={() => setView('subscribe')}>
              Get Event Updates
            </button>
          </div>
        </div>

        {selectedVendor && (
          <VendorDetailPopup vendor={selectedVendor} onClose={() => setSelectedVendor(null)} />
        )}
      </div>
    );
  }

  // Subscribe view
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.headerSmall}>
          <button style={styles.backBtn} onClick={() => setView('landing')}>← Back</button>
          <h2 style={styles.detailTitle}>Get Updates</h2>
          <p style={styles.detailSubtitle}>Receive announcements for {event.name}</p>
        </div>

        <div style={styles.body}>
          {subscribeResult ? (
            <div style={styles.successBox}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
              <p style={{ fontWeight: 600, marginBottom: '4px' }}>{subscribeResult}</p>
              <p style={{ fontSize: '0.8rem', color: '#666' }}>You can close this page now.</p>
              <button style={{ ...styles.btnSecondary, marginTop: '16px' }} onClick={() => { setSubscribeResult(''); setView('landing'); }}>
                Back to Event
              </button>
            </div>
          ) : (
            <>
              <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '20px', textAlign: 'center' }}>
                Enter your email or phone number to receive updates about this event. No account required.
              </p>

              {/* Contact type toggle */}
              <div style={styles.toggleRow}>
                <button
                  style={contactType === 'email' ? styles.toggleActive : styles.toggleInactive}
                  onClick={() => setContactType('email')}
                >
                  📧 Email
                </button>
                <button
                  style={contactType === 'phone' ? styles.toggleActive : styles.toggleInactive}
                  onClick={() => setContactType('phone')}
                >
                  📱 Phone
                </button>
              </div>

              <input
                type={contactType === 'email' ? 'email' : 'tel'}
                placeholder={contactType === 'email' ? 'your@email.com' : '+1 (555) 123-4567'}
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubscribe(); }}
                style={styles.input}
                aria-label={contactType === 'email' ? 'Email address' : 'Phone number'}
              />

              <button
                style={styles.btnPrimary}
                onClick={handleSubscribe}
                disabled={subscribing || !contactValue.trim()}
              >
                {subscribing ? 'Subscribing...' : 'Subscribe to Updates'}
              </button>

              <p style={{ fontSize: '0.72rem', color: '#999', textAlign: 'center', marginTop: '12px' }}>
                We'll only send updates about this event. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '16px',
    background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0d2418 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    marginTop: '20px',
  },
  loadingCard: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    maxWidth: '380px',
    width: '100%',
    marginTop: '60px',
  },
  errorCard: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    maxWidth: '380px',
    width: '100%',
    marginTop: '60px',
  },
  header: {
    background: 'linear-gradient(135deg, #1a6b3c, #0f4a28)',
    color: '#fff',
    padding: '28px 24px 20px',
    textAlign: 'center',
  },
  headerSmall: {
    background: 'linear-gradient(135deg, #1a6b3c, #0f4a28)',
    color: '#fff',
    padding: '16px 20px',
  },
  brandLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    opacity: 0.7,
    marginBottom: '4px',
  },
  eventName: {
    fontSize: '1.4rem',
    fontWeight: 800,
    margin: '4px 0',
    letterSpacing: '0.3px',
  },
  eventDate: {
    fontSize: '0.85rem',
    opacity: 0.9,
    marginTop: '6px',
  },
  eventTime: {
    fontSize: '0.8rem',
    opacity: 0.7,
  },
  body: {
    padding: '20px 24px 28px',
  },
  messageBar: {
    background: '#fffbeb',
    border: '1px solid #fbbf24',
    borderRadius: '10px',
    padding: '10px 14px',
    fontSize: '0.82rem',
    color: '#92400e',
    marginBottom: '16px',
  },
  btnPrimary: {
    display: 'block',
    width: '100%',
    padding: '16px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: '12px',
  },
  btnSecondary: {
    display: 'block',
    width: '100%',
    padding: '14px',
    backgroundColor: '#f0fdf4',
    color: '#1a6b3c',
    border: '2px solid #1a6b3c',
    borderRadius: '14px',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  infoSection: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  infoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
  },
  infoIcon: { fontSize: '1.3rem', flexShrink: 0 },
  infoLabel: { fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' },
  infoText: { fontSize: '0.88rem', fontWeight: 500, color: '#111827' },
  infoSubtext: { fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' },
  footer: { textAlign: 'center', fontSize: '0.7rem', color: '#999', marginTop: '24px' },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.85rem',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '6px',
  },
  detailTitle: { fontSize: '1.2rem', fontWeight: 700, margin: '0' },
  detailSubtitle: { fontSize: '0.8rem', opacity: 0.75, margin: '4px 0 0' },
  section: { marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' },
  sectionTitle: { fontSize: '0.9rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '8px' },
  sectionText: { fontSize: '0.88rem', color: '#333', lineHeight: 1.5 },
  sectionSubtext: { fontSize: '0.8rem', color: '#666' },
  mapLink: { display: 'inline-block', marginTop: '8px', fontSize: '0.82rem', color: '#1a6b3c', fontWeight: 600, textDecoration: 'none' },
  agendaItem: { display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' },
  agendaTime: { fontSize: '0.75rem', fontWeight: 700, color: '#1a6b3c', minWidth: '60px', paddingTop: '2px' },
  agendaTitle: { fontSize: '0.88rem', fontWeight: 600, color: '#111' },
  agendaSpeaker: { fontSize: '0.78rem', color: '#7c3aed', fontWeight: 500 },
  agendaDesc: { fontSize: '0.78rem', color: '#666', marginTop: '2px' },
  vendorGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  vendorChip: { padding: '8px 14px', backgroundColor: '#f3f4f6', borderRadius: '10px', border: '1px solid #e5e7eb', cursor: 'pointer', textAlign: 'left' as const, transition: 'background-color 0.15s, border-color 0.15s' },
  filterRow: { display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '10px', WebkitOverflowScrolling: 'touch' as any, scrollbarWidth: 'none' as any, msOverflowStyle: 'none' as any },
  filterPill: { flexShrink: 0, padding: '6px 12px', borderRadius: '16px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  filterPillActive: { flexShrink: 0, padding: '6px 12px', borderRadius: '16px', border: '1px solid #1a6b3c', backgroundColor: '#f0fdf4', color: '#1a6b3c', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  toggleRow: { display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'center' },
  toggleActive: { padding: '10px 20px', borderRadius: '20px', border: '2px solid #1a6b3c', backgroundColor: '#f0fdf4', color: '#1a6b3c', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' },
  toggleInactive: { padding: '10px 20px', borderRadius: '20px', border: '2px solid #e5e7eb', backgroundColor: '#fff', color: '#666', fontWeight: 500, fontSize: '0.88rem', cursor: 'pointer' },
  input: { width: '100%', padding: '16px', border: '2px solid #e5e7eb', borderRadius: '14px', fontSize: '1rem', marginBottom: '16px', boxSizing: 'border-box' },
  successBox: { textAlign: 'center', padding: '20px 0' },
};
